// Caret and range helpers for the block editor's contentEditable paragraphs.

export function currentRange(): Range | null {
  const sel = window.getSelection()
  return sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null
}

export function isCollapsed(): boolean {
  return currentRange()?.collapsed ?? true
}

export function textBeforeCaret(el: HTMLElement): string {
  const r = currentRange()
  if (!r || !el.contains(r.startContainer)) return el.textContent ?? ''
  const pre = document.createRange()
  pre.selectNodeContents(el)
  pre.setEnd(r.startContainer, r.startOffset)
  return pre.toString()
}

export function caretAtStart(el: HTMLElement): boolean {
  const r = currentRange()
  if (!r || !r.collapsed || !el.contains(r.startContainer)) return false
  return textBeforeCaret(el).length === 0
}

export function caretAtEnd(el: HTMLElement): boolean {
  const r = currentRange()
  if (!r || !r.collapsed || !el.contains(r.endContainer)) return false
  const post = document.createRange()
  post.selectNodeContents(el)
  post.setStart(r.endContainer, r.endOffset)
  return post.toString().length === 0
}

export function placeCaret(el: HTMLElement, where: 'start' | 'end'): void {
  el.focus({ preventScroll: true })
  const r = document.createRange()
  r.selectNodeContents(el)
  r.collapse(where === 'start')
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(r)
  el.scrollIntoView?.({ block: 'nearest' })
}

/** Put the caret `offset` characters into `el`'s text (at the end when the text is shorter). */
export function placeCaretAt(el: HTMLElement, offset: number): void {
  el.focus({ preventScroll: true })
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  const r = document.createRange()
  let pos = 0
  let placed = false
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
    if (offset <= pos + n.data.length) {
      r.setStart(n, Math.max(0, offset - pos))
      placed = true
      break
    }
    pos += n.data.length
  }
  if (!placed) {
    r.selectNodeContents(el)
    r.collapse(false)
  }
  r.collapse(true)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(r)
}

/** The HTML of `el` before (or after) a point in it; '' when no text is left. Leaves `el` alone. */
export function htmlBeside(el: HTMLElement, side: 'before' | 'after', node: Node, offset: number): string {
  const r = document.createRange()
  r.selectNodeContents(el)
  if (side === 'before') r.setEnd(node, offset)
  else r.setStart(node, offset)
  const box = document.createElement('div')
  box.appendChild(r.cloneContents())
  return (box.textContent ?? '') === '' ? '' : box.innerHTML
}

/** Cut everything after the caret out of `el`; returns it as HTML ('' when there is no text). */
export function splitAtCaret(el: HTMLElement): string {
  const r = currentRange()
  if (!r || !el.contains(r.endContainer)) return ''
  if (!r.collapsed) r.deleteContents()
  const tail = document.createRange()
  tail.selectNodeContents(el)
  tail.setStart(r.endContainer, r.endOffset)
  const box = document.createElement('div')
  box.appendChild(tail.extractContents())
  if ((el.textContent ?? '') === '') el.innerHTML = ''
  return (box.textContent ?? '') === '' ? '' : box.innerHTML
}

/** Delete the "/query" the user typed to open the insert menu. */
export function removeSlashToken(el: HTMLElement, query: string): void {
  const token = '/' + query
  const r = currentRange()
  if (r && r.collapsed && r.startContainer.nodeType === Node.TEXT_NODE && el.contains(r.startContainer)) {
    const node = r.startContainer as Text
    const off = r.startOffset
    if (off >= token.length && node.data.slice(off - token.length, off) === token) {
      const del = document.createRange()
      del.setStart(node, off - token.length)
      del.setEnd(node, off)
      del.deleteContents()
      if ((el.textContent ?? '') === '') el.innerHTML = ''
      return
    }
  }
  const html = el.innerHTML
  const i = html.lastIndexOf(token)
  if (i >= 0) el.innerHTML = html.slice(0, i) + html.slice(i + token.length)
}

/** Backspace at the start of a paragraph: move its content onto the end of `prev`, caret at the join. */
export function mergeInto(prev: HTMLElement, cur: HTMLElement): void {
  while (prev.lastChild && prev.lastChild.nodeName === 'BR') prev.removeChild(prev.lastChild)
  const joinAt = prev.childNodes.length
  while (cur.firstChild) {
    if (cur.firstChild.nodeName === 'BR' && cur.childNodes.length === 1) break
    prev.appendChild(cur.firstChild)
  }
  prev.focus({ preventScroll: true })
  const r = document.createRange()
  r.setStart(prev, joinAt)
  r.collapse(true)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(r)
}

// ── Selections as text offsets ─────────────────────────────────────────
// Unfolds hang from words the author selected. A live Range goes stale as
// soon as anything re-renders or the selection moves, so a selection is kept
// as character offsets into the block's text and turned back into a Range
// only when it's used.

export interface TextOffsets {
  start: number
  end: number
}

/** The current selection as offsets into `el`'s text; null unless it's a non-empty selection inside `el`. */
export function selectionOffsets(el: HTMLElement): TextOffsets | null {
  const r = currentRange()
  if (!r || r.collapsed || !el.contains(r.startContainer) || !el.contains(r.endContainer)) return null
  const pre = document.createRange()
  pre.selectNodeContents(el)
  pre.setEnd(r.startContainer, r.startOffset)
  const start = pre.toString().length
  const end = start + r.toString().length
  return end > start ? { start, end } : null
}

/** A Range over text offsets [start, end) of `el`, or null when they fall outside its text. */
export function rangeFromOffsets(el: HTMLElement, start: number, end: number): Range | null {
  if (start < 0 || end < start) return null
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  const range = document.createRange()
  let pos = 0
  let started = false
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
    const len = n.data.length
    if (!started && start < pos + len) {
      range.setStart(n, start - pos)
      started = true
    }
    if (started && end <= pos + len) {
      range.setEnd(n, end - pos)
      return range
    }
    pos += len
  }
  return null
}

const nodeLength = (n: Node) => (n.nodeType === Node.TEXT_NODE ? (n as Text).data.length : n.childNodes.length)
const indexIn = (n: Node) => Array.prototype.indexOf.call(n.parentNode!.childNodes, n) as number

/**
 * Move a range's ends out of inline elements they sit at the very edge of
 * (up to `root`), so wrapping "<em>very</em> thing" takes the whole <em>
 * instead of leaving an empty one behind.
 */
function liftBoundaries(range: Range, root: Node): void {
  let node = range.startContainer
  let off = range.startOffset
  while (node !== root && off === 0 && node.parentNode) {
    off = indexIn(node)
    node = node.parentNode
  }
  range.setStart(node, off)
  node = range.endContainer
  off = range.endOffset
  while (node !== root && off === nodeLength(node) && node.parentNode) {
    off = indexIn(node) + 1
    node = node.parentNode
  }
  range.setEnd(node, off)
}

/**
 * Wrap the text at offsets [start, end) of `el` in `<span data-unfold="anchor">`,
 * the words an unfold hangs from. Surrounding whitespace is left outside the
 * span. Returns the wrapped words, or null (and leaves `el` alone) when the
 * range is blank, out of bounds, or touches words that already unfold
 * something. Mutates `el`; read its innerHTML afterwards.
 */
export function wrapInUnfold(el: HTMLElement, start: number, end: number, anchor: string): string | null {
  const text = el.textContent ?? ''
  let s = Math.max(0, start)
  let e = Math.min(text.length, end)
  while (s < e && /\s/.test(text[s])) s++
  while (e > s && /\s/.test(text[e - 1])) e--
  if (e <= s) return null
  const range = rangeFromOffsets(el, s, e)
  if (!range) return null
  for (const existing of Array.from(el.querySelectorAll('span[data-unfold]'))) {
    if (range.intersectsNode(existing)) return null
  }
  liftBoundaries(range, el)
  const span = document.createElement('span')
  span.setAttribute('data-unfold', anchor)
  span.appendChild(range.extractContents())
  range.insertNode(span)
  // Drop the empty text nodes extractContents can leave either side.
  el.normalize()
  return span.textContent
}

/**
 * Wrap the text at offsets [start, end) of `el` (trimmed of surrounding
 * whitespace) in `wrapper`, taking out any `strip` elements inside it first so
 * they don't nest. Returns the wrapped words, or null when the range is blank
 * or out of bounds. Mutates `el`.
 */
function wrapOffsets(el: HTMLElement, start: number, end: number, wrapper: HTMLElement, strip: string): string | null {
  const text = el.textContent ?? ''
  let s = Math.max(0, start)
  let e = Math.min(text.length, end)
  while (s < e && /\s/.test(text[s])) s++
  while (e > s && /\s/.test(text[e - 1])) e--
  if (e <= s) return null
  const range = rangeFromOffsets(el, s, e)
  if (!range) return null
  // Whole elements of the stripped kind at the edges, so none is left half inside.
  const edge = (n: Node) => (n.nodeType === Node.ELEMENT_NODE ? (n as Element) : n.parentElement)?.closest(strip)
  const first = edge(range.startContainer)
  const last = edge(range.endContainer)
  if (first && el.contains(first)) range.setStartBefore(first)
  if (last && el.contains(last)) range.setEndAfter(last)
  liftBoundaries(range, el)
  const frag = range.extractContents()
  frag.querySelectorAll(strip).forEach((x) => x.replaceWith(...Array.from(x.childNodes)))
  wrapper.appendChild(frag)
  range.insertNode(wrapper)
  el.normalize()
  return wrapper.textContent
}

/**
 * Highlight the words at [start, end) of `el` as the ones a margin note is
 * about: `<mark data-note="anchor">`. Returns the words, or null.
 */
export function wrapInNoteMark(el: HTMLElement, start: number, end: number, anchor: string): string | null {
  const mark = document.createElement('mark')
  mark.setAttribute('data-note', anchor)
  return wrapOffsets(el, start, end, mark, 'mark')
}

/** Link the words at [start, end) of `el` to `href`. Returns the words, or null. */
export function wrapInLink(el: HTMLElement, start: number, end: number, href: string): string | null {
  const a = document.createElement('a')
  a.setAttribute('href', href)
  return wrapOffsets(el, start, end, a, 'a')
}

/** Take the links off whatever's at [start, end) of `el` (whole links, so none is left half done). */
export function unlinkOffsets(el: HTMLElement, start: number, end: number): void {
  const range = rangeFromOffsets(el, start, end)
  if (!range) return
  for (const a of Array.from(el.querySelectorAll('a'))) {
    if (range.intersectsNode(a)) a.replaceWith(...Array.from(a.childNodes))
  }
  el.normalize()
}

/** The link the words at [start, end) of `el` sit in, if any. */
export function linkAt(el: HTMLElement, start: number, end: number): string | null {
  const range = rangeFromOffsets(el, start, end)
  if (!range) return null
  for (const a of Array.from(el.querySelectorAll('a'))) {
    if (range.intersectsNode(a)) return a.getAttribute('href') ?? ''
  }
  return null
}

export function insertPlainText(text: string): void {
  // execCommand keeps the browser's undo stack intact; fall back to a manual insert.
  if (typeof document.execCommand === 'function' && document.execCommand('insertText', false, text)) return
  const r = currentRange()
  if (!r) return
  r.deleteContents()
  const node = document.createTextNode(text)
  r.insertNode(node)
  r.setStartAfter(node)
  r.collapse(true)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(r)
}
